import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ExternalLink, Copy, CheckCircle2, AlertTriangle, Sparkles, FileText, RefreshCw,
  LogIn, CornerDownLeft,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ title, children, testid, quiet }) => (
  <div className={quiet ? "rounded-lg border border-[#EDEAE3] bg-[#FBFAF7] p-3" : ""} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-semibold mb-1.5">{title}</div>
    {children}
  </div>
);

/* Similar tickets read like queue rows: the evidence, not a card wall. */
const RELEVANCE = {
  close: { background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" },
  related: { background: "#FFF7D1", border: "1px solid #E8C86A", color: "#6B5600" },
  loose: { background: "#F4F2ED", border: "1px solid #DAD5CA", color: "#59554D" },
};

const SimilarRow = ({ t, jiraUrl }) => (
  <a href={`${jiraUrl}/${t.key}`} target="_blank" rel="noopener noreferrer"
     className="block px-3 py-2 border-b border-[#F4F2ED] hover:bg-[#FBFAF7]"
     data-testid={`triage-similar-${t.key}`}>
    <div className="flex items-center gap-2 min-w-0">
      <span className="mono text-[11.5px] font-bold text-[#6B5600] shrink-0">{t.key}</span>
      <span className="text-[13px] text-[#1A1A1A] truncate flex-1">{t.summary || "—"}</span>
      {t.organisation && <span className="text-[11px] text-[#59554D] truncate max-w-[140px]">{t.organisation}</span>}
      <span className="mono text-[10px] font-bold px-1.5 rounded-full shrink-0"
        style={RELEVANCE[t.relevance] || RELEVANCE.loose}
        data-testid={`triage-relevance-${t.key}`}>
        {(t.relevance || "loose").toUpperCase()}
      </span>
      <span className="mono text-[10px] font-bold px-1.5 rounded-full shrink-0"
        style={t.resolved
          ? { background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" }
          : { background: "#F4F2ED", border: "1px solid #DAD5CA", color: "#59554D" }}>
        {t.resolved ? "RESOLVED" : (t.status || "OPEN").toUpperCase()}
      </span>
      <ExternalLink className="w-3 h-3 text-[#A39D8F] shrink-0" />
    </div>
    {t.match_reason && (
      <div className="text-[11px] text-[#8A857A] mt-0.5" data-testid={`triage-why-${t.key}`}>
        Why: {t.match_reason}
      </div>
    )}
    {(t.resolution || t.closing_comment) && (
      <div className="text-[11.5px] text-[#59554D] mt-0.5 line-clamp-2">
        {t.resolution ? <span className="font-semibold">{t.resolution}: </span> : null}
        {t.closing_comment}
      </div>
    )}
  </a>
);

export default function TriagePanel({ ticketKey, jiraUrl }) {
  const [evidence, setEvidence] = useState(null);
  const [suggest, setSuggest] = useState(null);
  const [loadingEvidence, setLoadingEvidence] = useState(true);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [failure, setFailure] = useState(null);      // {message, hint}
  const [attempt, setAttempt] = useState(0);
  const [copied, setCopied] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [thread, setThread] = useState([]);

  /* Agents have no access to Administration, so the sign-in has to live here —
     Atlassian brings them back to this app and they reopen the ticket. */
  const connectAtlassian = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/triage/atlassian/connect`, {
        params: { return_to: `/agent?triage=${encodeURIComponent(ticketKey)}` },
      });
      window.location.assign(r.data.authorize_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the Atlassian sign-in");
      setConnecting(false);
    }
  };

  const ask = async () => {
    const q = question.trim();
    if (!q || !evidence?.run_id) return;
    setAsking(true);
    setThread((t) => [...t, { role: "agent", text: q }]);
    setQuestion("");
    try {
      const r = await axios.post(`${API}/triage/ask`, { run_id: evidence.run_id, question: q });
      setThread((t) => [...t, {
        role: "assistant", text: r.data.answer || r.data.missing,
        cites: r.data.cites || [], answered: r.data.answered,
      }]);
    } catch (e) {
      setThread((t) => [...t, {
        role: "assistant", answered: false,
        text: e?.response?.data?.detail || "That question couldn't be answered just now.",
      }]);
    } finally { setAsking(false); }
  };

  useEffect(() => {
    let live = true;
    (async () => {
      setLoadingEvidence(true); setFailure(null); setEvidence(null); setSuggest(null);
      try {
        const r = await axios.post(`${API}/triage/evidence`, { ticket_key: ticketKey });
        if (!live) return;
        if (r.data?.ok === false) {
          setFailure(r.data.failure || { message: "Atlassian could not be read" });
          setLoadingEvidence(false);
          return;
        }
        setEvidence(r.data);
        setLoadingEvidence(false);
        setLoadingSuggest(true);
        try {
          const s = await axios.post(`${API}/triage/suggest`, { run_id: r.data.run_id });
          if (live) setSuggest(s.data);
        } catch (e) {
          if (live) toast.error("Couldn't generate the interpretation — the live evidence above still stands");
        } finally { if (live) setLoadingSuggest(false); }
      } catch (e) {
        if (!live) return;
        const d = e?.response?.data?.detail;
        setFailure(typeof d === "string" ? { message: d } : (d || { message: "Atlassian is unreachable" }));
        setLoadingEvidence(false);
      }
    })();
    return () => { live = false; };
  }, [ticketKey, attempt]);

  const s = suggest?.suggestions;
  const basedOn = suggest?.based_on;

  const copyReply = (text) => {
    navigator.clipboard.writeText(text || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="space-y-4" data-testid="triage-panel">
      {/* Connection failure — no keyword mirror to fall back on any more, so say
          exactly what is wrong and give the agent a way through. */}
      {failure && (
        <div className="rounded-lg border border-[#E8C86A] bg-[#FFF8D6] p-3" data-testid="triage-mcp-error">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#8A6D00] mt-0.5 shrink-0" />
            <div className="text-[12.5px] text-[#6B5600] min-w-0">
              <p className="font-semibold">Couldn't read {ticketKey} live from Atlassian.</p>
              <p className="mt-0.5">{failure.hint || failure.message}</p>
              <p className="mt-1 text-[#8A6D00]">
                Triage only reads live Atlassian data, so there is nothing to show until this is fixed.
                Work the ticket in Jira in the meantime.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => setAttempt((n) => n + 1)}
                  className="h-7 border-[#D9BE5E] bg-white text-[#6B5600] text-[11.5px]" data-testid="triage-retry">
                  <RefreshCw className="w-3 h-3 mr-1" /> Try again
                </Button>
                <a href={`${jiraUrl}/${ticketKey}`} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#6B5600] hover:underline"
                   data-testid="triage-open-jira">
                  Open {ticketKey} in Jira <ExternalLink className="w-3 h-3" />
                </a>
                {["not_connected", "reconnect_needed", "token_not_accepted",
                  "site_not_granted_to_token"].includes(failure.reason) && (
                  <Button size="sm" onClick={connectAtlassian} disabled={connecting}
                    className="h-7 bg-[#1A1A1A] hover:bg-[#333] text-white text-[11.5px]"
                    data-testid="triage-connect-link">
                    {connecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                : <LogIn className="w-3 h-3 mr-1" />}
                    Sign in to Atlassian
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1. Similar tickets — first and biggest */}
      {!failure && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="text-[13px] font-bold text-[#1A1A1A]">Similar tickets</h3>
            <div className="flex items-center gap-2">
              {evidence && (
                <span className="text-[11px] text-[#8A857A]" data-testid="triage-similar-count">
                  {evidence.similar_tickets?.length || 0} in {evidence.project}
                  {evidence.similar_tickets?.length
                    ? ` · ${evidence.similar_tickets.filter((t) => t.relevance === "close").length} close`
                    : ""}
                </span>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-[#DAD5CA] bg-white overflow-hidden">
            {loadingEvidence ? (
              <div className="flex items-center gap-2 px-3 py-4 text-[12.5px] text-[#59554D]">
                <Loader2 className="w-4 h-4 animate-spin" /> Reading {ticketKey} and searching the Service Desk…
              </div>
            ) : (evidence?.similar_tickets || []).length ? (
              (evidence.similar_tickets).map((t) => <SimilarRow key={t.key} t={t} jiraUrl={jiraUrl} />)
            ) : (
              <div className="px-3 py-4 text-[12.5px] text-[#8A857A]" data-testid="triage-no-similar">
                No comparable tickets found in {evidence?.project}. The suggestion below has little to go on.
              </div>
            )}
          </div>
          {evidence && (
            <p className="text-[11px] text-[#8A857A] mt-1" data-testid="triage-live-note">
              Read live from Atlassian at {new Date(evidence.read_at).toLocaleTimeString()} ·
              {" "}{evidence.comment_count} comment{evidence.comment_count === 1 ? "" : "s"} on this ticket
              {evidence.ticket?.status ? ` · ${evidence.ticket.status}` : ""}
            </p>
          )}
        </div>
      )}

      {/* 2. Interpretation — quieter, because the tickets are the evidence */}
      {!failure && (
        <div className="space-y-3">
          {loadingSuggest && (
            <div className="flex items-center gap-2 text-[12.5px] text-[#59554D]" data-testid="triage-suggest-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Working through the thread and the matches…
            </div>
          )}
          {s?.insufficient_evidence && (
            <div className="rounded-lg border border-[#DAD5CA] bg-[#F4F2ED] p-3 text-[12.5px] text-[#59554D]"
              data-testid="triage-insufficient">
              Not enough to go on here — {s.evidence_note || "no close matches and no relevant pages"}.
              Treat this ticket as new.
            </div>
          )}
          {s && (
            <>
              <Section title="What's happening" testid="triage-analysis" quiet>
                <p className="text-[12.5px] text-[#1A1A1A]">{s.issue_analysis || "—"}</p>
              </Section>
              {!!(s.possible_causes || []).length && (
                <Section title="Likely cause" testid="triage-causes" quiet>
                  <ol className="list-decimal ml-4 space-y-0.5 text-[12.5px] text-[#1A1A1A]">
                    {s.possible_causes.map((c, i) => <li key={i}>{c}</li>)}
                  </ol>
                </Section>
              )}
              {!!(s.troubleshooting_steps || []).length && (
                <Section title="Next actions" testid="triage-steps" quiet>
                  <ol className="list-decimal ml-4 space-y-0.5 text-[12.5px] text-[#1A1A1A]">
                    {s.troubleshooting_steps.map((c, i) => <li key={i}>{c}</li>)}
                  </ol>
                </Section>
              )}
              {s.suggested_response && (
                <Section title="Draft reply" testid="triage-reply" quiet>
                  <p className="text-[12.5px] text-[#1A1A1A] whitespace-pre-wrap">{s.suggested_response}</p>
                  <Button size="sm" variant="outline" onClick={() => copyReply(s.suggested_response)}
                    className="h-7 mt-2 border-[#DAD5CA] text-[#59554D] text-[11.5px]" data-testid="triage-copy-reply">
                    {copied ? <CheckCircle2 className="w-3 h-3 mr-1 text-[#0E6B3F]" /> : <Copy className="w-3 h-3 mr-1" />}
                    {copied ? "Copied" : "Copy reply"}
                  </Button>
                </Section>
              )}
            </>
          )}

          {/* 3. Documentation — titles and links, not pasted content */}
          {!!(evidence?.documents || []).length && (
            <Section title="Documentation" testid="triage-docs">
              <div className="space-y-1">
                {evidence.documents.map((d, i) => (
                  <a key={i} href={d.url || "#"} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-1.5 text-[12.5px] text-[#1D4ED8] hover:underline"
                     data-testid={`triage-doc-${i}`}>
                    <FileText className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{d.title}</span>
                    {d.space && <span className="text-[11px] text-[#8A857A]">· {d.space}</span>}
                  </a>
                ))}
              </div>
            </Section>
          )}

          {/* 4. Ask about this ticket — answered from the evidence above */}
          {evidence?.run_id && (
            <Section title="Ask about this ticket" testid="triage-ask">
              <p className="text-[11.5px] text-[#8A857A] mb-1.5">
                Answered from this ticket, the matching tickets and the pages above — it says when
                the evidence doesn't cover the question rather than guessing.
              </p>
              {!!thread.length && (
                <div className="space-y-2 mb-2" data-testid="triage-ask-thread">
                  {thread.map((m, i) => (
                    <div key={i} className={m.role === "agent" ? "text-right" : ""}>
                      <div className={`inline-block max-w-[92%] rounded-lg px-2.5 py-1.5 text-[12.5px] text-left ${
                        m.role === "agent"
                          ? "bg-[#1A1A1A] text-white"
                          : m.answered === false
                            ? "bg-[#FFF8D6] border border-[#E8C86A] text-[#6B5600]"
                            : "bg-white border border-[#DAD5CA] text-[#1A1A1A]"}`}
                        data-testid={`triage-ask-${m.role}-${i}`}>
                        <p className="whitespace-pre-wrap break-words">{m.text}</p>
                        {!!(m.cites || []).length && (
                          <p className="text-[11px] text-[#8A857A] mt-1">From: {m.cites.join(", ")}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {asking && (
                    <div className="flex items-center gap-1.5 text-[11.5px] text-[#8A857A]">
                      <Loader2 className="w-3 h-3 animate-spin" /> Reading the evidence…
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input value={question} onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
                  placeholder="Has this happened before? What fixed it last time?"
                  className="h-8 border-[#DAD5CA] text-[12.5px]" data-testid="triage-ask-input" />
                <Button size="sm" onClick={ask} disabled={asking || !question.trim()}
                  className="h-8 bg-[#1A1A1A] hover:bg-[#333] text-white shrink-0"
                  data-testid="triage-ask-submit">
                  {asking ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <CornerDownLeft className="w-3.5 h-3.5" />}
                  <span className="ml-1.5 text-[11.5px]">Ask</span>
                </Button>
              </div>
            </Section>
          )}

          {/* 5. Shown its working */}
          {(basedOn || evidence) && (
            <div className="text-[11px] text-[#8A857A] border-t border-[#EDEAE3] pt-2" data-testid="triage-based-on">
              <span className="font-semibold text-[#6B6660]">Based on: </span>
              {basedOn?.similar_tickets?.length
                ? `${basedOn.similar_tickets.length} ticket${basedOn.similar_tickets.length === 1 ? "" : "s"} (${basedOn.similar_tickets.join(", ")}${basedOn.resolved_matches?.length ? `, ${basedOn.resolved_matches.length} resolved` : ""})`
                : "no similar tickets"}
              {" · "}
              {basedOn?.ticket_comments ?? evidence?.comment_count ?? 0} comment(s) on this ticket
              {" · "}
              {(basedOn?.documents?.length || evidence?.documents?.length || 0)} page(s)
              {" · read "}{new Date(basedOn?.read_at || evidence?.read_at).toLocaleString()}
              {s?.confidence && (
                <Badge className="ml-2 text-[10px]"
                  style={s.confidence === "high" ? { background: "#E3F3E9", color: "#0E6B3F" }
                    : s.confidence === "medium" ? { background: "#FFF7D1", color: "#6B5600" }
                    : { background: "#F4F2ED", color: "#59554D" }}>
                  {s.confidence} confidence
                </Badge>
              )}
              {suggest?.ai_generated && (
                <span className="ml-1.5 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI interpretation</span>
              )}
            </div>
          )}
          {!!(evidence?.warnings || []).length && (
            <ul className="text-[11px] text-[#8A6D00] space-y-0.5" data-testid="triage-warnings">
              {evidence.warnings.map((w, i) => <li key={i}>· {w}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Lead's proof: old keyword result beside the live one */}
    </div>
  );
}
